import {Injectable, signal} from '@angular/core';
import {BehaviorSubject} from 'rxjs';
import {Params} from '@angular/router';
import {CommonTableData} from '@lib-ui/common-table/models/common-table-data.class';
import {MessageService} from '@lib-services/message.service';
import {IPerson, IPersonTableData} from '@lib-typing/person.interface';
import {PersonService} from '@lib-services/person.service';
import {ICommonTableConfig, ICommonTableService} from '@lib-ui/common-table/models/common-table.interface';
import {IPaginatedResponse} from '@lib-typing/response.interface';
import {Role} from '@libs-enums/role.enum';
import { EnumService } from '@lib-services/enum.service';

@Injectable({
    providedIn: 'root',
})
export class PersonTableService implements ICommonTableService<IPersonTableData> {
    public tableData$: BehaviorSubject<CommonTableData<IPersonTableData>> =
        new BehaviorSubject<CommonTableData<IPersonTableData>>(new CommonTableData());
    defaultFilterParams = {
        sortBy: 'createdAt:DESC',
    };
    private readonly commonTableData = new CommonTableData();
    loading = signal<boolean>(false);

    constructor(private personService: PersonService, private messageService: MessageService, private enumService: EnumService) {}

    updateData(config: ICommonTableConfig): void {
        this.loading.set(true);
        this.personService
            .getList(this.createSearchParams(config))
            .subscribe({
                next: response => {
                    this.tableData$.next(new CommonTableData(this.transformData(response)));
                },
                error: () => this.messageService.errorMessage('Ошибка загрузки списка'),
            })
            .add(() => this.loading.set(false));
    }

    createSearchParams(config: ICommonTableConfig): Params {
        this.commonTableData.data.length = 0;
        const { pageIndex, itemsPerPage } = config.paginatorConfig;
        const { filters } = config;

        let searchParams: Params = {
            ...this.defaultFilterParams,
            limit: itemsPerPage.toString(),
        };

        if (pageIndex) {
            searchParams = {
                ...searchParams,
                page: pageIndex.toString(),
            };
        }

        if (filters) {
            Object.entries(filters).forEach(([key, value]) => {
                if (!value) {
                    return;
                }

                switch (key) {
                case 'location.address':
                    searchParams[`filter.${key}`] = `$ilike:${value}`;
                    break;
                case 'name':
                    // По полю name одновременно ищем совпадения и в имени и в фамилии
                    searchParams['filter.firstName'] = `$ilike:${value}`;
                    searchParams['filter.lastName'] = `$ilike:${value}`;
                    break;
                default:
                    searchParams[`filter.${key}`] = `${value}`;
                    break;
                }
            });
        }

        return searchParams;
    }

    transformData(response: IPaginatedResponse<IPerson>): IPaginatedResponse<IPersonTableData> {
        const transformedData = response.data.map(person => {
            return {
                id: person.id,
                role: this.enumService.getTranslatedRole(person.role),
                groupName: (person.group?.name && person.role === Role.executor) ? person.group.name : '',
                fullName: `${person.firstName || ''} ${person.lastName || ''}`,
            } as IPersonTableData;

        });

        return {
            ...response,
            data: transformedData
        };
    }
}
